import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Grid } from '@mui/material'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Select from '../../../../components/forms/Select'
import SimpleDatePicker from '../../../../components/forms/SimpleDatePicker'
import RadioButtons from '../../../../components/forms/RadioButtons'
import Checkbox from '../../../../components/forms/Checkbox'
import { CardTable } from '../../../../components/datatable/NewTable'
import { getIntegrationModes } from '../../../../utils/JobUtils'
import { DATA_ADD_LAST_DATA } from '../../../../traitements/constants/TraitementConstants'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import { darkBlue, mainWhite } from '../../../../utils/constants/ColorTheme'
import IndicatorsExplanationDialog from '../IndicatorsExplanationDialog'
import IndicatorsObjectsDialog from '../IndicatorsObjectsDialog'
import IntegrationModeDialog from '../IntegrationModeDialog'
import ResourceAction from '../../../../resource/actions/ResourceAction'
import ProductionUnitAction from '../../../../productionUnit/actions/ProductionUnitAction'
import Job from '../../../dto/DtoJob'
import { CALCULATION_MODES, INDICATOR_OBJECT_TYPE, INDICATOR_TYPE, getIndicatorTypes, getObjectTypes } from '../../../constants/IndicatorsConstants'

const IndicatorsCalculationPanel = ({
    job,
    isEditMode = false,
    onChangeJob = () => {},
}) => {
    const dispatch = useDispatch()
    const [selectionIsOpen, setSelectionIsOpen] = useState(false)

    const {
        resources,
        productionUnits,
    } = useSelector(store => ({
        resources: store.ResourceReducer.resources,
        productionUnits: store.ProductionUnitReducer.productionUnits,
    }), shallowEqual)

    const filters = useMemo(() => ({
        objectType: INDICATOR_OBJECT_TYPE.RESOURCE,
        indicatorType: INDICATOR_TYPE.WATER_STRESS,
        calculationMode: CALCULATION_MODES.CLASSIC,
        importMode: DATA_ADD_LAST_DATA,
        objectCodes: [],
        ...(job.parameters.filters.length ? JSON.parse(job.parameters.filters[0]) : {}),
    }), [job])

    useEffect(() => {
        if (!resources.length) {
            dispatch(ResourceAction.fetchResources())
        }
        if (!productionUnits.length) {
            dispatch(ProductionUnitAction.fetchProductionUnits())
        }
    }, [])

    const onChangeFilters = changes => onChangeJob({
        parameters: {
            ...job.parameters,
            filters: [JSON.stringify({ ...filters, ...changes })],
        },
    })

    const isProductionUnit = filters.objectType === INDICATOR_OBJECT_TYPE.PRODUCTION_UNIT
    const objects = isProductionUnit ? productionUnits : resources
    const objectLabel = isProductionUnit ? i18n.productionUnits : i18n.resources

    const rows = useMemo(() => filters.objectCodes.map(code => ({
        code,
        name: objects.find(object => object.code === code)?.name ?? '',
    })), [filters.objectCodes, objects])

    const disabled = !isEditMode

    const helpIcon = icon => <span style={{ display: 'block', height: 0 }}>{icon}</span>

    return (
        <Grid container spacing={1}>
            <Grid size={12}>
                <StyledFieldSet>
                    <StyledLegend>{i18n.calculType}</StyledLegend>
                    <Grid container spacing={2} alignItems='center' sx={{ padding: 1 }}>
                        <Grid size={3}>
                            <Select
                                label={i18n.calculationObject}
                                value={filters.objectType}
                                options={getObjectTypes()}
                                onChange={v => onChangeFilters({ objectType: v, objectCodes: [] })}
                                noNullValue
                                integerValue
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Select
                                label={i18n.indicatorType}
                                labelSpan={helpIcon(<IndicatorsExplanationDialog />)}
                                value={filters.indicatorType}
                                options={getIndicatorTypes()}
                                onChange={v => onChangeFilters({ indicatorType: v })}
                                noNullValue
                                integerValue
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Select
                                label={i18n.dataIntegration}
                                labelSpan={helpIcon(<IntegrationModeDialog />)}
                                value={filters.importMode}
                                options={getIntegrationModes()}
                                onChange={v => onChangeFilters({ importMode: v })}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid size={3}>
                            <RadioButtons
                                title={i18n.calculationMode}
                                elements={[
                                    { code: CALCULATION_MODES.CLASSIC, name: i18n.classic },
                                    { code: CALCULATION_MODES.IAEAU, name: i18n.iaeau },
                                ]}
                                selected={filters.calculationMode}
                                onChange={v => onChangeFilters({ calculationMode: v })}
                                disabled={disabled}
                            />
                        </Grid>
                    </Grid>
                </StyledFieldSet>
            </Grid>
            <Grid size={12}>
                <StyledFieldSet>
                    <StyledLegend>{i18n.periode}</StyledLegend>
                    <Grid container spacing={2} alignItems='center' sx={{ padding: 1 }}>
                        <Grid size={3}>
                            <SimpleDatePicker
                                label={i18n.startDate}
                                value={filters.startDate}
                                onChange={v => onChangeFilters({ startDate: v })}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid size={3}>
                            <SimpleDatePicker
                                label={i18n.endDate}
                                value={filters.endDate}
                                onChange={v => onChangeFilters({ endDate: v })}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Checkbox
                                checked={!!filters.validOnly}
                                label={i18n.validatedDataOnly}
                                onChange={v => onChangeFilters({ validOnly: v })}
                                disabled={disabled}
                            />
                        </Grid>
                    </Grid>
                </StyledFieldSet>
            </Grid>
            <Grid size={12}>
                <CardTable
                    title={objectLabel}
                    headers={['code', 'name']}
                    headersLabel={{ code: i18n.code, name: i18n.name }}
                    rows={rows}
                    lineActions={[
                        { icon: 'delete', onClick: row => onChangeFilters({ objectCodes: filters.objectCodes.filter(code => code !== row.code) }), displayed: isEditMode },
                    ]}
                    actions={[
                        { icon: 'add', onClick: () => setSelectionIsOpen(true), tooltip: i18n.add, color: mainWhite, displayed: isEditMode },
                    ]}
                    color={darkBlue}
                />
            </Grid>
            <IndicatorsObjectsDialog
                open={selectionIsOpen}
                label={objectLabel}
                objects={objects}
                selectedCodes={filters.objectCodes}
                onClose={() => setSelectionIsOpen(false)}
                onValidate={objectCodes => {
                    onChangeFilters({ objectCodes })
                    setSelectionIsOpen(false)
                }}
            />
        </Grid>
    )
}

IndicatorsCalculationPanel.propTypes = {
    job: PropTypes.instanceOf(Job).isRequired,
    isEditMode: PropTypes.bool,
    onChangeJob: PropTypes.func,
}

export default IndicatorsCalculationPanel